import { fabric } from 'fabric';
import { AssetHandler } from './assetHandler';
import { ObjectHandler } from './objectHandler';
import TextHandler from './textHandler';
import { updateCFBucketUrls } from 'utils';

const OBJECT_TYPES = {
  IMAGE: ['MTGImage', 'image'],
  TEXT: ['text', 'textbox'],
  OBJECT: ['path', 'DynamicLine', 'polygon', 'ellipse', 'rect', 'circle'],
};

class CanvasHandler {
  constructor(content, data) {
    this.tempCanvas = new fabric.Canvas();
    this.content = content;
    this.data = data;
  }

  async generateSVG() {
    return new Promise((resolve, reject) => {
      this.tempCanvas.loadFromJSON(updateCFBucketUrls(this.content), async () => {
        try {
          const { result, externalResources } = await this.getSVGFromCanvas();
          resolve({ result, externalResources });
        } catch (error) {
          console.error('Error generating SVG from Canvas:', error);
          reject(error);
        }
      });
    });
  }

  async getSVGFromCanvas() {
    const bounds = this.getCanvasBounds();
    const objects = this.tempCanvas.getObjects();
    const { assets, texts, fabricObjects } = this.getCanvasObjectsByType(objects);

    const assetHandler = new AssetHandler(this.tempCanvas, bounds, assets);
    const processedAssets = await assetHandler.extractAssets();
    const externalResources = assetHandler.getExternalResources();

    const textHandler = new TextHandler(this.tempCanvas, bounds, this.data.preserveTexts);
    const processedTextElements = await textHandler.extractTextElements(texts);

    const objectHandler = new ObjectHandler(this.tempCanvas, bounds, fabricObjects);
    const processedObjects = objectHandler.extractObjects();

    const finalElements = [...processedAssets, ...processedTextElements, ...processedObjects].sort(
      (a, b) => a.zIndex - b.zIndex,
    );

    // Replace 'transparent' with 'none' for the background fill
    const bgFill = bounds.backgroundColor === 'transparent' ? 'none' : bounds.backgroundColor;

    // Create the SVG with the properly formatted elements
    const result = `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        width="${bounds.width}"
        height="${bounds.height}"
        viewBox="0 0 ${bounds.width} ${bounds.height}"
        overflow="hidden"
      >
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="${bgFill}"
        />
        ${finalElements
          .map((asset) => {
            // Process content to replace transparent with none
            if (asset?.content) {
              return asset.content.replace(/fill="transparent"/g, 'fill="none"');
            }
            return '';
          })
          .join('\n')}
      </svg>
    `.trim();

    assetHandler.clearCache();

    return {
      result,
      externalResources,
    };
  }

  getCanvasObjectsByType(objects, zIndex = 0, groupCenter = { x: 0, y: 0 }, parentGroup = null) {
    const assets = [];
    const texts = [];
    const fabricObjects = [];

    objects.forEach((obj) => {
      // Use the actual object index from the canvas for proper z-ordering

      if (obj.type === 'group') {
        const objCenterPoint = obj.getCenterPoint();
        const {
          assets: gAssets,
          texts: gTexts,
          fabricObjects: gFabricObjects,
          lastIndex,
        } = this.getCanvasObjectsByType(
          obj.getObjects(),
          zIndex,
          { x: groupCenter.x + objCenterPoint.x, y: groupCenter.y + objCenterPoint.y },
          obj,
        );

        // Only add group children if the group and its children should be visible
        // Check for composite operations that might hide content
        const shouldIncludeGroupContent = this.shouldIncludeGroupContent(obj, parentGroup);

        if (shouldIncludeGroupContent) {
          assets.push(...gAssets);
          texts.push(...gTexts);
          fabricObjects.push(...gFabricObjects);
        }
        zIndex = lastIndex;
      } else if (OBJECT_TYPES.IMAGE.includes(obj.type)) {
        obj.left = obj.left + groupCenter.x;
        obj.top = obj.top + groupCenter.y;

        // Check if this object should be included based on composite operation
        if (this.shouldIncludeObject(obj, parentGroup)) {
          assets.push({
            src: obj.src,
            object: obj,
            zIndex,
            parentGroup: parentGroup,
          });
        }
      } else if (OBJECT_TYPES.TEXT.includes(obj.type)) {
        obj.left = obj.left + groupCenter.x;
        obj.top = obj.top + groupCenter.y;

        // Check if this object should be included based on composite operation
        if (this.shouldIncludeObject(obj, parentGroup)) {
          texts.push({
            object: obj,
            zIndex,
            parentGroup: parentGroup,
          });
        }
      } else if (OBJECT_TYPES.OBJECT.includes(obj.type)) {
        obj.left = obj.left + groupCenter.x;
        obj.top = obj.top + groupCenter.y;

        // Check if this object should be included based on composite operation
        if (this.shouldIncludeObject(obj, parentGroup)) {
          fabricObjects.push({
            object: obj,
            zIndex,
            parentGroup: parentGroup,
          });
        }
      }
      zIndex += 1;
    });

    return { assets, texts, fabricObjects, lastIndex: zIndex };
  }

  shouldIncludeGroupContent(group, parentGroup) {
    // If group has source-atop composite operation, it might be clipped by other elements
    if (group.globalCompositeOperation === 'source-atop') {
      // Check if there are any visible elements above this group that could serve as a clipping mask
      const allObjects = this.tempCanvas.getObjects();
      const groupIndex = allObjects.indexOf(group);

      // Look for objects that come after this group and could serve as clipping sources
      for (let i = groupIndex + 1; i < allObjects.length; i++) {
        const laterObject = allObjects[i];
        if (this.objectsOverlap(group, laterObject) && laterObject.visible !== false) {
          // There's an overlapping object that comes later, so the group content should be clipped
          return false;
        }
      }
    }

    return true;
  }

  shouldIncludeObject(obj, parentGroup) {
    // If object has source-atop composite operation and is in a group
    if (obj.globalCompositeOperation === 'source-atop' && parentGroup) {
      // Check if the parent group is effectively clipped by later objects
      const allObjects = this.tempCanvas.getObjects();
      const groupIndex = allObjects.indexOf(parentGroup);

      // Look for objects that come after the parent group
      for (let i = groupIndex + 1; i < allObjects.length; i++) {
        const laterObject = allObjects[i];
        if (this.objectsOverlap(parentGroup, laterObject) && laterObject.visible !== false) {
          // There's an overlapping object that comes later, so this text should be hidden
          return false;
        }
      }
    }

    return obj.visible !== false;
  }

  objectsOverlap(obj1, obj2) {
    // Simple bounding box overlap check
    const bounds1 = obj1.getBoundingRect();
    const bounds2 = obj2.getBoundingRect();

    return !(
      bounds1.left + bounds1.width < bounds2.left ||
      bounds2.left + bounds2.width < bounds1.left ||
      bounds1.top + bounds1.height < bounds2.top ||
      bounds2.top + bounds2.height < bounds1.top
    );
  }

  getCanvasBounds() {
    const board = this.tempCanvas.backgroundImage;

    if (!board) {
      console.warn('No background board found, using canvas dimensions');
      return {
        x: 0,
        y: 0,
        width: this.tempCanvas.width,
        height: this.tempCanvas.height,
        backgroundColor: 'none',
        actualLeft: 0,
        actualTop: 0,
      };
    }

    return {
      x: 0,
      y: 0,
      width: board.width,
      height: board.height,
      backgroundColor: board.fill === 'transparent' ? 'none' : board.fill || 'none',
      actualLeft: board.left,
      actualTop: board.top,
    };
  }
}

export default CanvasHandler;
